import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import type { DayData } from 'voyager';
import { useRoomCalendarAvailabilityForRange } from '@features/rooms';
import {
  AvailabilityRange,
  initialAvailabilityRange,
  monthStart,
  rangeFor,
} from './availabilityRange';
import { toDayDataMap } from './toDayDataMap';

const DATE_FORMAT = 'YYYY-MM-DD';

type Params = {
  adults: number;
  children: number;
  initialMonth?: string;
  rateCode?: string;
  roomCode?: string;
  enabled?: boolean;
};

export function useJourneyPickerAvailability({
  adults,
  children,
  initialMonth,
  rateCode,
  roomCode,
  enabled = true,
}: Params) {
  const [range, setRange] = useState<AvailabilityRange>(() =>
    initialAvailabilityRange(initialMonth)
  );
  const [rangeScope, setRangeScope] = useState({ rateCode, roomCode });

  if (rangeScope.rateCode !== rateCode || rangeScope.roomCode !== roomCode) {
    setRangeScope({ rateCode, roomCode });
    setRange(initialAvailabilityRange(initialMonth));
  }

  useEffect(() => {
    if (!initialMonth) return;
    const target = monthStart(initialMonth);
    setRange((current) =>
      target.isSame(current.start, 'month') ? current : initialAvailabilityRange(initialMonth)
    );
  }, [initialMonth]);

  const startDate = range.start.format(DATE_FORMAT);
  const endDate = range.end.format(DATE_FORMAT);

  const { data: availabilityRecords, isFetching } = useRoomCalendarAvailabilityForRange(
    {
      startDate,
      endDate,
      adults,
      children,
      rateCode,
      roomCode,
    },
    enabled
  );

  const merged = useRef<{ scope: string; days: Map<string, DayData> }>({
    scope: '',
    days: new Map(),
  });

  const availability = useMemo(() => {
    const scope = [adults, children, rateCode ?? '', roomCode ?? ''].join('-');
    const previous = merged.current.scope === scope ? merged.current.days : new Map();
    const days = new Map([...previous, ...toDayDataMap(availabilityRecords ?? [])]);
    merged.current = { scope, days };
    return days;
  }, [availabilityRecords, adults, children, rateCode, roomCode]);

  const handleMonthChange = useCallback((firstVisibleMonth: Date, lastVisibleMonth: Date) => {
    const start = moment(firstVisibleMonth).startOf('month');
    const last = moment(lastVisibleMonth).startOf('month');
    const next = last.isBefore(start) ? rangeFor(start) : { start, end: last.endOf('month') };

    setRange((current) =>
      next.start.isSame(current.start, 'month') && next.end.isSame(current.end, 'month')
        ? current
        : next
    );
  }, []);

  return {
    availability,
    isLoadingAvailability: isFetching,
    onMonthChange: handleMonthChange,
  };
}
